import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { environment } from '../../../../environment/environment';

@Injectable({ providedIn: 'root' })
export class ChatService {
  private ws: WebSocket | null = null;
  private stream$ = new Subject<any>();
  isClosed = false;

  private readonly backendBaseUrl = environment.chatApiBaseUrl;
  private readonly websocketBaseUrl = environment.chatWsBaseUrl;


  constructor() {}

  /**
   * Securely connect to chat backend
   * 1. Calls /auth/session to obtain signed token
   * 2. Opens WebSocket with that token
   */
  async connectSecure(): Promise<void> {
    try {
      this.isClosed = false; // ✅ reset before connecting

      // Step 1: Request a signed session token from backend
      const resp = await fetch(`${this.backendBaseUrl}/auth/session`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        // include credentials if backend uses cookies later
      });

      if (!resp.ok) {
        throw new Error(`Auth failed: ${resp.status}`);
      }

      const data = await resp.json();
      const token = data.token;
      const sessionId = data.session_id;

      if (!token || !sessionId) {
        throw new Error('Invalid session response from backend');
      }

      // Step 2: Connect to secure WebSocket endpoint
      this.ws = new WebSocket(`${this.websocketBaseUrl}?token=${token}`);
      
      // Step 3: Wire up event listeners
      this.ws.onopen = () => {
        this.isClosed = false; // ✅ mark connected again
      };

      this.ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          this.stream$.next(msg);
        } catch {
           }
      };

      this.ws.onclose = (ev) => {
        
        this.isClosed = true; // 👈 mark closed
      };

      this.ws.onerror = (err) => {
        
        this.isClosed = true; // 👈 mark closed
      };
    } catch (err) {
      
      this.isClosed = true; // 👈 mark closed
    }
  }

  async reconnect(): Promise<void> {
    
    await this.connectSecure();
  }

  /**
   * Send prompt to backend
   */
  sendPrompt(prompt: string) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'start', prompt }));
    } else {
      
      this.isClosed = true; // mark closed so UI shows reconnect button
    }
  }

  /**
   * Observable stream of chat messages
   */
  onMessage(): Observable<any> {
    return this.stream$.asObservable();
  }
}
